import { abis, EnvioGraphQLClient, getArtifact, MultipassBase } from '@peeramid-labs/sdk';
import { Web3ProviderInterface } from '../types';
import useNotification from './useNotification';
import { processError } from '../utils/processError';
import { Address, ContractFunctionArgs, PublicClient, WalletClient, zeroAddress, zeroHash } from 'viem';
import { type WriteContractErrorType } from '@wagmi/core';
import { useEffect, useState } from 'react';
import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';

export type useMultipassProps = {
  chainId: number;
  publicClient: PublicClient;
  walletClient: WalletClient;
  envioClient: EnvioGraphQLClient;
  account: Address;
  domainName: string;
};

export const useMultipass = (props: useMultipassProps) => {
  const [hasMultipassRegistered, setHasMultipassRegistered] = useState<boolean | null>(null);
  const [isRecordResolutionFetched, setIsRecordResolutionFetched] = useState<boolean>(false);
  const notification = useNotification();

  //**************Multipass resolve record*****************
  const multipassBase = new MultipassBase({
    chainId: props.chainId,
    publicClient: props.publicClient,
    envioClient: props.envioClient,
  });

  const multipassContractAddress: Address = getArtifact(props.chainId, 'Multipass').address;

  const resolveRecordReadContract = useReadContract({
    abi: abis.MultipassAbi,
    address: multipassContractAddress,
    functionName: 'resolveRecord',
    args: [
      multipassBase.formQueryByAddress({
        address: props.account,
        domainName: props.domainName,
        useBytes32: true,
      }),
    ],
  });

  useEffect(() => {
    if (resolveRecordReadContract.isSuccess) {
      if (
        !resolveRecordReadContract.data ||
        !resolveRecordReadContract.data[1].name ||
        !resolveRecordReadContract.data[0]
      ) {
        setHasMultipassRegistered(false);
      } else {
        setHasMultipassRegistered(true);
      }
    }
  }, [resolveRecordReadContract.isSuccess, resolveRecordReadContract.isFetching]);

  useEffect(() => {
    if (resolveRecordReadContract.isError) {
      processError(resolveRecordReadContract.error, abis.MultipassAbi, notification);
      setHasMultipassRegistered(false);
    }
  }, [resolveRecordReadContract.isError]);

  useEffect(() => {
    if (hasMultipassRegistered !== null) {
      setIsRecordResolutionFetched(true);
    }
  }, [hasMultipassRegistered]);

  //**************Multipass signup*****************
  const registerWriteContract = useWriteContract();

  const signUp = async ({
    message,
    registrarSignature,
  }: {
    message: ContractFunctionArgs<typeof abis.IMultipassAbi, 'payable', 'register'>[0];
    registrarSignature: Address;
  }) => {
    if (!props.chainId) throw new Error('No chain selected');
    const emptyUserQuery: ContractFunctionArgs<typeof abis.IMultipassAbi, 'payable', 'register'>[2] = {
      name: zeroHash,
      id: zeroHash,
      domainName: zeroHash,
      wallet: zeroAddress,
      targetDomain: zeroHash,
    };

    registerWriteContract.writeContract(
      {
        abi: abis.MultipassAbi,
        address: multipassContractAddress,
        functionName: 'register',
        args: [message, registrarSignature, emptyUserQuery, zeroHash],
        // gas: 250_00n,
      },
      {
        onSettled: async (a, b, c, d) => {
          console.log(a, b, c, d);
        },
        onError: (error: WriteContractErrorType) => {
          processError(error, abis.MultipassAbi, notification);
          registerWriteContract.reset();
        },
      }
    );
  };

  const {
    isLoading: isSignUpTransactionLoading,
    isSuccess: isSignUpTransactionSuccess,
    isError: isSignUpTransactionError,
    error: signupTransactionError,
  } = useWaitForTransactionReceipt({
    hash: registerWriteContract.data,
  });

  useEffect(() => {
    if (isSignUpTransactionSuccess) {
      resolveRecordReadContract.refetch();
      notification('Access to fellowship granted successfully', 'success');
    } else if (isSignUpTransactionError) {
      processError(signupTransactionError, abis.MultipassAbi, notification);
      console.error(signupTransactionError);
    }
  }, [isSignUpTransactionSuccess, isSignUpTransactionError]);

  return {
    hasMultipassRegistered,
    isRecordResolutionFetched,
    isSignUpPending: registerWriteContract.isPending || isSignUpTransactionLoading,
    isSignUpSuccess: isSignUpTransactionSuccess,
    isSignUpError: isSignUpTransactionError,
    signUp,
  };
};

export const generateInputProps = ({
  web3ProviderContext,
  account,
  domainName,
}: {
  web3ProviderContext: Web3ProviderInterface;
  account?: Address | null;
  domainName?: string;
}) => {
  if (!web3ProviderContext.currentChain) {
    throw new Error('Current chain not initialized');
  }
  if (!web3ProviderContext.account) {
    throw new Error('Account not initialized');
  }

  return {
    chainId: web3ProviderContext.currentChain.id,
    publicClient: web3ProviderContext.publicClient,
    walletClient: web3ProviderContext.walletClient,
    account: account ?? web3ProviderContext.account,
    envioClient: web3ProviderContext.envioClient,
    domainName: domainName,
  } as useMultipassProps;
};
